import CircularProgress from '@material-ui/core/CircularProgress'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { Card } from '../../../components/Base/Base'
import { InlineSpinner } from '../../../components/Spinner'
import { HandlerOf } from '../../../helpers/typeHelper'
import { useJobs } from '../../../hooks/query/useJobs'
import {
  getDefaultJobStatusDetailValue,
  Job,
  JOB_STATUS_DICT,
  JOBS_SPLIT_LIST_PAGE_SIZE,
  JobStatus,
  JobStatusDetail,
} from '../../../models/job'
import { User } from '../../../models/user'
import { ProfileJobOrderBy, ProfileJobOrderByDirection } from './ProfileJobs'
import { ProfileJobsHeader } from './ProfileJobsHeader'
import { ProfileJobsList } from './ProfileJobsList'

interface ProfileJobsSectionProps {
  user: User
  handleJobOpen: HandlerOf<Job>
  jobUrlConstructor?: (job: Job) => string | undefined
  isInsideSidebar?: boolean
  splitStatus?: JobStatus
}

export function ProfileJobsSection({
  user,
  handleJobOpen,
  jobUrlConstructor,
  isInsideSidebar,
  splitStatus,
}: ProfileJobsSectionProps) {
  const [statusDetailFilter, setStatusDetailFilter] = useState<JobStatusDetail[]>(
    getDefaultJobStatusDetailValue()
  )
  const [orderBy, setOrderBy] = useState<ProfileJobOrderBy>(ProfileJobOrderBy.PICK_UP_DATE)
  const [orderByDirection, setOrderByDirection] = useState<ProfileJobOrderByDirection>('-') // "-" is descending

  const {
    jobs,
    jobsPages,
    count,
    isLoading,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    isFetching,
    refetch,
  } = useJobs(
    { enabled: true },
    {
      assignedTo: user.id,
      // in sidebar only show current, future, pending jobs
      statusDetail: !!splitStatus
        ? // for past jobs hide ABORTED
          JOB_STATUS_DICT[splitStatus].filter((x) => x !== JobStatusDetail.ABORTED)
        : statusDetailFilter,
      orderBy: getOrderBy(orderBy, orderByDirection),
      ...(splitStatus ? { pageSize: JOBS_SPLIT_LIST_PAGE_SIZE } : {}),
    }
  )

  useEffect(() => {
    refetch()
  }, [orderBy, orderByDirection, statusDetailFilter, refetch])

  return (
    <StyledCard
      hasSubtleShadow
      smallerPadding
      align="left"
      padding={isInsideSidebar ? (splitStatus ? '12px 0px' : '0px') : undefined}
      $isInsideSidebar={isInsideSidebar}
      mb={!!splitStatus ? 3 : 0}
    >
      <ProfileJobsHeader
        user={user}
        statusDetailFilter={statusDetailFilter}
        setStatusDetailFilter={setStatusDetailFilter}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        orderByDirection={orderByDirection}
        setOrderByDirection={setOrderByDirection}
        hasJobs={jobs && jobs.length > 0}
        hiddenStatusOptions={isInsideSidebar ? ['PAST'] : undefined}
        isInsideSidebar={isInsideSidebar}
        onlyOneStatus={splitStatus}
        count={count}
      />
      {isLoading || !jobs ? (
        <InlineSpinner mt={5} mb={5} />
      ) : (
        <ProfileJobsList
          jobs={jobs}
          jobsPages={jobsPages}
          orderBy={orderBy}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          hasPreviousPage={hasPreviousPage}
          fetchPreviousPage={fetchPreviousPage}
          isFetching={isFetching}
          handleJobOpen={handleJobOpen}
          jobUrlConstructor={jobUrlConstructor}
          isInsideSidebar={isInsideSidebar}
          hideNoJobsMessage={!!splitStatus}
          useNumberedPagination={!!splitStatus}
          totalPages={Math.ceil((count ?? 0) / JOBS_SPLIT_LIST_PAGE_SIZE)}
        />
      )}

      {!isLoading && jobs && isFetching && (
        <LoadingContainer>
          <CircularProgress color="secondary" size={56} />
        </LoadingContainer>
      )}
    </StyledCard>
  )
}

function getOrderBy(orderBy: ProfileJobOrderBy, orderByDirection: ProfileJobOrderByDirection) {
  if (orderBy === ProfileJobOrderBy.PICK_UP_DATE) {
    return `${orderByDirection}load__origin_pickup_date`
  }

  if (orderBy === ProfileJobOrderBy.DROPOFF_DATE) {
    return `${orderByDirection}load__dest_delivery_date`
  }

  if (orderBy === ProfileJobOrderBy.DATE_LAST_MODIFIED) {
    return `${orderByDirection}date_updated`
  }

  if (orderBy === ProfileJobOrderBy.STATUS) {
    return 'status_detail'
  }

  if (orderBy === ProfileJobOrderBy.BROKER) {
    return 'load__broker__name'
  }
}

const StyledCard = styled(Card)<{ $isInsideSidebar?: boolean; $isConsole?: boolean }>`
  ${({ $isInsideSidebar }) =>
    $isInsideSidebar &&
    css`
      .load-item-container {
        grid-template-columns: 1fr;
        grid-template-areas:
          'tags'
          'path'
          'path'
          'broker';

        .load-list-rate,
        .load-list-bookmark,
        .load-list-equipment {
          display: none;
        }

        .multistop-path-item-location {
          grid-template-columns: auto 1fr;
        }
      }
    `}

  position: relative;
  overflow: hidden;
`

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);

  display: flex;
  justify-content: center;
  align-items: center;
`
